import classNames from 'classnames';
import React from 'react';
import { useIntl } from 'react-intl';
import { Virtuoso } from 'react-virtuoso';
import { ChipProps } from 'src/components/reusable-components/Chip';
import MobileJobCard from 'src/components/reusable-components/mobile-job-card/MobileJobCard';
import { DqText } from '../../../components/dq-text';
import { JobMessages } from '../../../intl/JobMessages';

export interface JobsListInfiniteItem {
  /** The page to route to on select. */
  routerLink: string;
  /** The filename of the log file. */
  filename: string;
  /** The serial number of the controller that opened the log. */
  serialNumber: string;
  /** The title of the card. */
  title: string;
  /** The name of the creator of the job. */
  createdByName: string;
  /** The title and type of the status */
  chipProps: ChipProps;
  /** When the job was created. */
  createdAt: number;
  /** The timezone associated with the job. */
  timezone?: string;
  /** The size of the job log.  */
  linesParsed: number;
  /** The status of the job log, used for grouping. */
  status: 'complete' | 'in-progress' | 'live';
  /** Indicates if source is local */
  isLocal: boolean;
}

interface JobsListInfiniteProps {
  /** The list of jobs to display. */
  jobs: JobsListInfiniteItem[];
  /** The total number of jobs in the list, including any jobs not yet fetched. */
  totalCount: number;
  /** Indicates if the entire result set was loaded */
  loadedEntireResults: boolean;
}

const renderItem = (index: number, job: JobsListInfiniteItem) => {
  return (
    <div className={classNames('ion-padding-top')}>
      <MobileJobCard
        routerLink={job.routerLink}
        title={job.title}
        createdByName={job.createdByName}
        createdAt={job.createdAt}
        timezone={job.timezone}
        chipProps={job.chipProps}
        linesParsed={job.linesParsed ?? 0}
        isLocal={job.isLocal}
      />
    </div>
  );
};

const keyAccessor = (index: number, job: JobsListInfiniteItem) => job.routerLink;

/** A presentation component which renders the list of jobs using an infinite scroll strategy. */
export const JobsListInfinite: React.FC<JobsListInfiniteProps> = props => {
  const { formatMessage } = useIntl();

  const grouped = React.useMemo(() => {
    const live: JobsListInfiniteItem[] = [];
    const inProgress: JobsListInfiniteItem[] = [];
    const complete: JobsListInfiniteItem[] = [];
    for (const item of props.jobs) {
      if (item.status === 'live') live.push(item);
      else if (item.status === 'in-progress') inProgress.push(item);
      else complete.push(item);
    }
    return [...live, ...inProgress, ...complete];
  }, [props.jobs]);

  return (
    <Virtuoso<JobsListInfiniteItem>
      data={grouped}
      totalCount={props.totalCount}
      itemContent={renderItem}
      computeItemKey={keyAccessor}
      components={{
        Footer: () => {
          return (
            <div className="ion-text-center ion-padding-top">
              <DqText variant={'h3'}>
                {props.loadedEntireResults
                  ? formatMessage(JobMessages.endOfResultsMessage)
                  : formatMessage(JobMessages.endOfJobsListMessage)}
              </DqText>
            </div>
          );
        },
      }}
    />
  );
};
